import { z } from "zod";

import { mapLegacyEhrPayerNameKey } from "@/src/models/documents/referral";
import { insuranceTierSnapshotSchema } from "@/src/models/insurance-tier-snapshot";

export { insuranceTierSnapshotSchema, type InsuranceTierSnapshot } from "@/src/models/insurance-tier-snapshot";

export const referralTierSchema = z.enum(["Tier 1", "Tier 2", "Not Accepted", "Needs review"]);
export const referralEligibilityInputSchema = z.preprocess(
  mapLegacyEhrPayerNameKey,
  z.object({
    primaryEhrPayerName: z.string().nullable(),
    receivingLocation: z.string().nullable(),
    serviceLine: z.string().nullable(),
    isPostOp: z.boolean(),
    diagnoses: z.array(z.object({ code: z.string().nullable(), text: z.string().nullable() })),
    bodyParts: z.array(z.string()),
    reasonForReferral: z.string().nullable(),
    ageMayBeMinor: z.boolean(),
  })
);
export type ReferralEligibilityInput = z.infer<typeof referralEligibilityInputSchema>;

export const referralClinicalAssessmentSchema = z.object({
  excludedServices: z.array(z.string()),
  pediatricNeuroConditions: z.array(z.string()),
  cardiacOrPulmonaryDiagnosis: z.boolean().nullable(),
});
export type ReferralClinicalAssessment = z.infer<typeof referralClinicalAssessmentSchema>;

export const referralTierDecisionSchema = z.object({
  tier: referralTierSchema,
  reasons: z.array(z.string()),
});
export type ReferralTierDecision = z.infer<typeof referralTierDecisionSchema>;
export const referralTierOutputSchema = referralTierDecisionSchema.extend({
  patientId: z.string().min(1),
  evaluatedAt: z.iso.datetime({ offset: true }),
  input: referralEligibilityInputSchema,
  clinical: referralClinicalAssessmentSchema,
  snapshot: insuranceTierSnapshotSchema.omit({ rows: true }).nullable(),
});
export type ReferralTierOutput = z.infer<typeof referralTierOutputSchema>;

export const smsEligibilityDecisionSchema = z.object({
  eligible: z.boolean(),
  needsReview: z.boolean(),
  reasons: z.array(z.string()),
});
export type SmsEligibilityDecision = z.infer<typeof smsEligibilityDecisionSchema>;
export const smsEligibilityOutputSchema = smsEligibilityDecisionSchema.extend({
  patientEhrId: z.string().min(1),
  patientCaseEhrId: z.string().min(1),
  patientCreated: z.boolean(),
  caseCreated: z.boolean().nullable(),
  tier: referralTierSchema,
  evaluatedAt: z.iso.datetime({ offset: true }),
});
export type SmsEligibilityOutput = z.infer<typeof smsEligibilityOutputSchema>;

export const referralSyncContextSchema = smsEligibilityOutputSchema.pick({
  patientEhrId: true,
  patientCaseEhrId: true,
  patientCreated: true,
  caseCreated: true,
});
export type ReferralSyncContext = z.infer<typeof referralSyncContextSchema>;

/** Task-independent assessment returned to workflows and recurring eligibility checks. */
export const referralEligibilityAssessmentSchema = z.object({
  tier: referralTierOutputSchema,
  sync: referralSyncContextSchema.nullable(),
  sms: smsEligibilityDecisionSchema,
});
export type ReferralEligibilityAssessment = z.infer<typeof referralEligibilityAssessmentSchema>;
